import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useScrollAnimation, useScrollAnimationMultiple } from "@/hooks/useScrollAnimation";
import type React from "react";

const testimonials = [
  {
    titleJa: "名前を整えるって、こんなに自然なんですね",
    titleEn: "Harmonizing a name feels so natural",
    titleIt: "Armonizzare un nome è così naturale",
    titleFr: "Harmoniser un nom, c'est si naturel",
    titleDe: "Einen Namen zu harmonisieren fühlt sich so natürlich an",
    titleEs: "Armonizar un nombre se siente tan natural",
    titleRu: "Гармонизировать имя — это так естественно",
    titleZh: "调整名字，竟然如此自然",
    titleKo: "이름을 정돈한다는 게, 이렇게 자연스러운 거군요",
    titlePt: "Harmonizar um nome é tão natural assim",
    titleAr: "تنسيق الاسم يبدو طبيعياً جداً",
    titleHi: "नाम को संतुलित करना इतना स्वाभाविक है",
    titleTh: "การปรับสมดุลชื่อรู้สึกเป็นธรรมชาติมาก",
    titleVi: "Điều hòa tên cảm thấy rất tự nhiên",
    titleId: "Menyelaraskan nama terasa sangat alami",
    titleTl: "Ang pag-aayos ng pangalan ay nakakaramdam ng napaka-natural",
    titleNe: "नाम मिलाउनु यति स्वाभाविक लाग्छ",
    titleZhTW: "調整名字，竟然如此自然",
    location: "港区", locationEn: "Minato-ku", locationIt: "Minato-ku", locationFr: "Minato-ku", locationDe: "Minato-ku", locationEs: "Minato-ku", locationRu: "Минато-ку", locationZh: "港区", locationKo: "미나토구", locationPt: "Minato-ku", locationAr: "مينتو-كو", locationHi: "मिनातो-कु", locationTh: "มินาโตะ-คุ", locationVi: "Minato-ku", locationId: "Minato-ku", locationTl: "Minato-ku", locationNe: "मिनातो-कु", locationZhTW: "港區",
    age: "30代", ageEn: "30s", ageIt: "30 anni", ageFr: "30 ans", ageDe: "30er", ageEs: "30 años", ageRu: "30-е", ageZh: "30多岁", ageKo: "30대", agePt: "30 anos", ageAr: "الثلاثينيات", ageHi: "30 के दशक", ageTh: "อายุ 30 ปี", ageVi: "30 tuổi", ageId: "30-an", ageTl: "30s", ageNe: "३० को दशक", ageZhTW: "30多歲",
    job: "ライフスタイルメディア編集", jobEn: "Lifestyle Media Editor", jobIt: "Editor Media Lifestyle", jobFr: "Éditrice Médias Lifestyle", jobDe: "Lifestyle-Medienredakteurin", jobEs: "Editora de Medios de Estilo de Vida", jobRu: "Редактор лайфстайл-медиа", jobZh: "生活方式媒体编辑", jobKo: "라이프스타일 미디어 편집", jobPt: "Editora de Mídia de Estilo de Vida", jobAr: "محررة وسائل إعلام أسلوب الحياة", jobHi: "लाइफस्टाइल मीडिया संपादक", jobTh: "บรรณาธิการสื่อไลฟ์สไตล์", jobVi: "Biên tập viên Lifestyle", jobId: "Agen Periklanan", jobTl: "Editor ng Lifestyle Media", jobNe: "लाइफस्टाइल मीडिया सम्पादक", jobZhTW: "官民合作顧問",
    textJa: "忙しさに流されがちな毎日ですが、名前に「てんてん」を添えていただいてから、不思議と呼吸が深くなった気がします。大げさな変化ではなく、あくまで「自分のペースのまま」心が整う感じが心地いいです。",
    textEn: "My days tend to get swept away by busyness, but since having ten-ten added to my name, I feel like my breathing has somehow deepened. It's not a dramatic change — it's the comfort of my heart settling at my own pace.",
    textIt: "Le mie giornate tendono a essere travolte dalla frenesia, ma da quando ho aggiunto i ten-ten al mio nome, sento che il mio respiro si è in qualche modo approfondito.",
    textFr: "Même dans mes journées chargées, je remarque des moments de légèreté.",
    textDe: "Meine Tage werden oft von der Hektik mitgerissen, aber seit Ten-ten zu meinem Namen hinzugefügt wurde, habe ich das Gefühl, dass mein Atem tiefer geworden ist.",
    textEs: "Mis días tienden a ser arrastrados por el ajetreo, pero desde que se añadieron ten-ten a mi nombre, siento que mi respiración se ha profundizado.",
    textRu: "Мои дни часто захлёстывает суета, но с тех пор как к моему имени добавили тен-тен, я чувствую, что моё дыхание как-то углубилось.",
    textZh: "每天都容易被忙碌所淹没，但自从名字添加了浊点之后，不知为何感觉呼吸变深了。",
    textKo: "바쁜 일상에 휩쓸리기 쉬운 매일이지만, 이름에 휩점을 더해주신 후로 신기하게도 호흡이 깊어진 것 같습니다.",
    textPt: "Meus dias tendem a ser varridos pela agitação, mas desde que ten-ten foi adicionado ao meu nome, sinto que minha respiração de alguma forma se aprofundou.",
    textAr: "أيامي تميل إلى الانجراف في الانشغال، لكن منذ إضافة التنتن إلى اسمي، أشعر أن تنفسي قد تعمق بطريقة ما.",
    textHi: "मेरे दिन व्यस्तता में बह जाते हैं, लेकिन जब से मेरे नाम में तेंतेन जोड़ा गया, मुझे लगता है कि मेरी सांस किसी तरह गहरी है।",
    textTh: "วันของฉันมักถูกกลืนหายไปกับความวุ่นวาย แต่ตั้งแต่เพิ่มเทนเทนลงในชื่อ ฉันรู้สึกว่าการหายใจของฉันลึกขึ้น",
    textVi: "Những ngày của tôi thường bị cuốn vào sự bận rộn, nhưng kể từ khi thêm tenten vào tên, tôi cảm thấy hơi thở của mình sâu hơn.",
    textId: "Hari-hari saya cenderung tersapu oleh kesibukan, tetapi sejak tenten ditambahkan ke nama saya, saya merasa napas saya entah bagaimana menjadi lebih dalam.",
    textTl: "Ang mga araw ay madalas na nadadala ng abala, ngunit mula nang idagdag ang tenten sa aking pangalan, pakiramdam ko ay lumalalim ang aking paghinga.",
    textNe: "सायद म आफैलाई थाहा नभई जोड गरिरहेको थिएँ। आफ्नो नाम हेर्ने समय एक प्रकारको हृदय मर्मत बन्यो।",
    textZhTW: "即使在忙碌的每一天，也會在某個瞬間感受到「輕盈」。感覺能以比以前更溫柔的心情面對孩子和丈夫。",
  },
  {
    titleJa: "選択に迷う時間が短くなりました",
    titleEn: "I spend less time hesitating over decisions",
    titleIt: "Passo meno tempo a esitare sulle decisioni",
    titleFr: "Je passe moins de temps à hésiter sur mes décisions",
    titleDe: "Ich zögere weniger bei Entscheidungen",
    titleEs: "Paso menos tiempo dudando sobre decisiones",
    titleRu: "Я трачу меньше времени на колебания при принятии решений",
    titleZh: "做决定时犹豫的时间变短了",
    titleKo: "선택에 망설이는 시간이 짧아졌습니다",
    titlePt: "Passo menos tempo hesitando nas decisões",
    titleAr: "أقضي وقتاً أقل في التردد على القرارات",
    titleHi: "निर्णय लेने में कम समय लगता है",
    titleTh: "ฉันใช้เวลาลังเลในการตัดสินใจน้อยลง",
    titleVi: "Tôi ít do dự hơn khi đưa ra quyết định",
    titleId: "Saya lebih sedikit ragu dalam mengambil keputusan",
    titleTl: "Mas kaunti na akong nag-aalangan sa paggawa ng desisyon",
    titleNe: "निर्णय लेने में कम समय लगता है",
    titleZhTW: "做決定時猶豫的時間變短了",
    location: "横浜", locationEn: "Yokohama", locationIt: "Yokohama", locationFr: "Yokohama", locationDe: "Yokohama", locationEs: "Yokohama", locationRu: "Йокогама", locationZh: "横浜", locationKo: "요코하마", locationPt: "Yokohama", locationAr: "يوكوهاما", locationHi: "योकोहामा", locationTh: "โยโกฮาม่า", locationVi: "Yokohama", locationId: "Yokohama", locationTl: "Yokohama", locationNe: "योकोहामा", locationZhTW: "橫濱",
    age: "40代", ageEn: "40s", ageIt: "40 anni", ageFr: "40 ans", ageDe: "40er", ageEs: "40 años", ageRu: "40-е", ageZh: "40多岁", ageKo: "40대", agePt: "40 anos", ageAr: "الأربعينيات", ageHi: "40 के दशक", ageTh: "อายุ 40 ปี", ageVi: "40 tuổi", ageId: "40-an", ageTl: "40s", ageNe: "४० को दशक", ageZhTW: "40多歲",
    job: "医療関連会社管理職", jobEn: "Healthcare Manager", jobIt: "Manager Sanitario", jobFr: "Responsable Santé", jobDe: "Gesundheitsmanager", jobEs: "Gerente de Salud", jobRu: "Менеджер здравоохранения", jobZh: "医疗相关公司管理职", jobKo: "의료 관련 회사 관리직", jobPt: "Gerente de Saúde", jobAr: "مدير رعاية صحية", jobHi: "स्वास्थ्य सेवा प्रबंधक", jobTh: "ผู้จัดการด้านสุขภาพ", jobVi: "Quản lý Y tế", jobId: "Manajer Kesehatan", jobTl: "Tagapamahala ng Kalusugan", jobNe: "स्वास्थ्य सेवा प्रबंधक", jobZhTW: "官民合作顧問",
    textJa: "日々、判断が求められる仕事ですが、最近は「雑音」が減ったというか、迷いすぎずスッと決断できるように。自分を信じる感覚が増えました。静かな自信が育つイメージです。",
    textEn: "My work demands constant decisions, but lately the 'noise' has decreased — I can decide without overthinking. My sense of self-trust has grown. It feels like a quiet confidence is taking root.",
    textIt: "Il mio lavoro richiede decisioni costanti, ma ultimamente il 'rumore' è diminuito — riesco a decidere senza pensarci troppo.",
    textFr: "Mon travail exige des décisions constantes, mais dernièrement le bruit a diminué — je peux décider sans trop réfléchir.",
    textDe: "Meine Arbeit erfordert ständige Entscheidungen, aber in letzter Zeit hat das Rauschen abgenommen.",
    textEs: "Mi trabajo exige decisiones constantes, pero últimamente el ruido ha disminuido — puedo decidir sin pensar demasiado.",
    textRu: "Моя работа требует постоянных решений, но в последнее время «шум» уменьшился.",
    textZh: "工作中每天都需要做判断，但最近感觉「杂音」减少了，不再过度犹豫，能够干脆地做决定。",
    textKo: "매일 판단이 요구되는 일이지만, 최근에는 '잡음'이 줄었다고 할까, 너무 망설이지 않고 바로 결단할 수 있게 되었습니다.",
    textPt: "Meu trabalho exige decisões constantes, mas ultimamente o 'ruído' diminuiu — posso decidir sem pensar muito.",
    textAr: "حتى في أيامي المزدحمة، ألاحظ لحظات من الخفة.",
    textHi: "अपने व्यस्त दिनों में भी, मुझे हल्केपन के पल मिलते हैं।",
    textTh: "แม้ในวันที่วุ่นวาย ฉันก็สังเกตเห็นช่วงเวลาแห่งความเบาสบาย",
    textVi: "Ngay cả trong những ngày bận rộn, tôi nhận thấy những khoảnh khắc nhẹ nhàng.",
    textId: "Hari-hari saya cenderung tersapu oleh kesibukan, tetapi sejak tenten ditambahkan ke nama saya, saya merasa napas saya entah bagaimana menjadi lebih dalam.",
    textTl: "Ang mga araw ay madalas na nadadala ng abala, ngunit mula nang idagdag ang tenten sa aking pangalan, pakiramdam ko ay lumalalim ang aking paghinga.",
    textNe: "मेरा दिनहरू व्यस्ततामा बग्ने गर्छन्, तर नाममा टेन्टेन थपिएपछि, अनौठो रूपमा मेरो सास गहिरो भएको जस्तो लाग्छ।",
    textZhTW: "工作性質決定了語言非常重要。正因如此，重新審視自己名字的時間，出乎意料地有著重大意義。",
  },
  {
    titleJa: "子育ての「余白」が生まれました",
    titleEn: "Space appeared in my parenting",
    titleIt: "È apparso spazio nella mia genitorialità",
    titleFr: "Un espace est apparu dans ma parentalité",
    titleDe: "In meiner Elternschaft ist Raum entstanden",
    titleEs: "Apareció espacio en mi crianza",
    titleRu: "В моём родительстве появилось пространство",
    titleZh: "育儿中出现了「余裕」",
    titleKo: "육아에 '여유'가 생겼습니다",
    titlePt: "Um espaço surgiu na minha criação dos filhos",
    titleAr: "ظهرت مساحة في تربيتي لأطفالي",
    titleHi: "बच्चों की परवरिश में 'जगह' मिली",
    titleTh: "มีพื้นที่ว่างปรากฏขึ้นในการเลี้ยงลูก",
    titleVi: "Không gian xuất hiện trong việc nuôi dạy con",
    titleId: "Ruang muncul dalam pengasuhan anak saya",
    titleTl: "Lumitaw ang espasyo sa aking pag-aalaga ng anak",
    titleNe: "बच्चा पालनमा 'ठाउँ' देखा पर्यो",
    titleZhTW: "育兒中出現了「餘裕」",
    location: "世田谷", locationEn: "Setagaya", locationIt: "Setagaya", locationFr: "Setagaya", locationDe: "Setagaya", locationEs: "Setagaya", locationRu: "Сэтагая", locationZh: "世田谷", locationKo: "세타가야", locationPt: "Setagaya", locationAr: "سينتاغا", locationHi: "सेतागाया", locationTh: "เซตากายะ", locationVi: "Setagaya", locationId: "Setagaya", locationTl: "Setagaya", locationNe: "सेतागाया", locationZhTW: "世田谷",
    age: "30代", ageEn: "30s", ageIt: "30 anni", ageFr: "30 ans", ageDe: "30er", ageEs: "30 años", ageRu: "30-е", ageZh: "30多岁", ageKo: "30대", agePt: "30 anos", ageAr: "الثلاثينيات", ageHi: "30 के दशक", ageTh: "อายุ 30 ปี", ageVi: "30 tuổi", ageId: "30-an", ageTl: "30s", ageNe: "३० को दशक", ageZhTW: "30多歲",
    job: "2児の母", jobEn: "Mother of 2", jobIt: "Madre di 2 figli", jobFr: "Mère de 2 enfants", jobDe: "Mutter von 2 Kindern", jobEs: "Madre de 2 hijos", jobRu: "Мать двоих детей", jobZh: "两个孩子的妈妈", jobKo: "두 아이의 엄마", jobPt: "Mãe de 2 filhos", jobAr: "أم لطفلين", jobHi: "2 बच्चों की माँ", jobTh: "แม่ลูก 2 คน", jobVi: "Mẹ của 2 con", jobId: "Ibu dari 2 anak", jobTl: "Ina ng 2 anak", jobNe: "२ बच्चाकी आमा", jobZhTW: "兩個孩子的媽媽",
    textJa: "慌ただしい毎日でも、ふとした瞬間に「軽さ」を感じるように。子どもにも、夫にも、以前よりやわらかい気持ちで向き合えている気がします。暮らしの中に、やさしい余白ができました。",
    textEn: "Even in my hectic days, I notice moments of lightness. I feel I can face my children and husband with a softer heart than before. A gentle space has appeared in my daily life.",
    textIt: "Anche nelle mie giornate frenetiche, noto momenti di leggerezza.",
    textFr: "Même dans mes journées chargées, je remarque des moments de légèreté.",
    textDe: "Auch in meinen hektischen Tagen bemerke ich Momente der Leichtigkeit.",
    textEs: "Incluso en mis días ajetreados, noto momentos de ligereza.",
    textRu: "Даже в мои суматошные дни я замечаю моменты лёгкости.",
    textZh: "即使在忙碌的每一天，也会在某个瞬间感受到「轻盈」。",
    textKo: "바빠도 즐길 수 있는 성격이지만, 최근 왠지 마음에 노이즈가 많아서요.",
    textPt: "Mesmo nos meus dias agitados, noto momentos de leveza.",
    textAr: "حتى في أيامي المزدحمة، ألاحظ لحظات من الخفة.",
    textHi: "अपने व्यस्त दिनों में भी, मुझे हल्केपन के पल मिलते हैं।",
    textTh: "แม้ในวันที่วุ่นวาย ฉันก็สังเกตเห็นช่วงเวลาแห่งความเบาสบาย",
    textVi: "Ngay cả trong những ngày bận rộn, tôi nhận thấy những khoảnh khắc nhẹ nhàng.",
    textId: "Bahkan di hari-hari sibuk saya, saya memperhatikan momen-momen keringanan.",
    textTl: "Ang mga salita ay napakahalaga sa aking trabaho.",
    textNe: "सायद म आफैलाई थाहा नभई जोड गरिरहेको थिएँ।",
    textZhTW: "工作性質決定了語言非常重要。正因如此，重新審視自己名字的時間，出乎意料地有著重大意義。",
  },
  {
    titleJa: "肩に入っていた力が抜けていく感じ",
    titleEn: "The tension in my shoulders is melting away",
    titleIt: "La tensione nelle mie spalle si sta sciogliendo",
    titleFr: "La tension dans mes épaules se dissipe",
    titleDe: "Die Spannung in meinen Schultern löst sich",
    titleEs: "La tensión en mis hombros se está disolviendo",
    titleRu: "Напряжение в плечах растворяется",
    titleZh: "肩膀上的力气慢慢松开了",
    titleKo: "어깨에 들어가 있던 힘이 빠지는 느낌",
    titlePt: "A tensão nos meus ombros está se dissolvendo",
    titleAr: "التوتر في كتفيّ يذوب تدريجياً",
    titleHi: "कंधों का तनाव पिघल रहा है",
    titleTh: "ความตึงเครียดที่ไหล่ค่อยๆ ละลายหายไป",
    titleVi: "Sự căng thẳng ở vai đang tan biến dần",
    titleId: "Sakitnya leher perlahan menghilang",
    titleTl: "Ang tensyon sa aking mga balikat ay unti-unting natutunaw",
    titleNe: "काँधको तनाव बिस्तारै पग्लिँदैछ",
    titleZhTW: "肩膀上的力氣慢慢鬆開了",
    location: "名古屋", locationEn: "Nagoya", locationIt: "Nagoya", locationFr: "Nagoya", locationDe: "Nagoya", locationEs: "Nagoya", locationRu: "Нагоя", locationZh: "名古屋", locationKo: "나고야", locationPt: "Nagoya", locationAr: "ناغويا", locationHi: "नागोया", locationTh: "นาโกย่า", locationVi: "Nagoya", locationId: "Nagoya", locationTl: "Nagoya", locationNe: "नागोया", locationZhTW: "名古屋",
    age: "30代", ageEn: "30s", ageIt: "30 anni", ageFr: "30 ans", ageDe: "30er", ageEs: "30 años", ageRu: "30-е", ageZh: "30多岁", ageKo: "30대", agePt: "30 anos", ageAr: "الثلاثينيات", ageHi: "30 के दशक", ageTh: "อายุ 30 ปี", ageVi: "30 tuổi", ageId: "30-an", ageTl: "30s", ageNe: "३० को दशक", ageZhTW: "30多歲",
    job: "ライフスタイルメディア編集", jobEn: "Lifestyle Media Editor", jobIt: "Editor Media Lifestyle", jobFr: "Éditrice Médias Lifestyle", jobDe: "Lifestyle-Medienredakteurin", jobEs: "Editora de Medios de Estilo de Vida", jobRu: "Редактор лайфстайл-медиа", jobZh: "生活方式媒体编辑", jobKo: "라이프스타일 미디어 편집", jobPt: "Editora de Mídia de Estilo de Vida", jobAr: "محررة وسائل إعلام أسلوب الحياة", jobHi: "लाइफस्टाइल मीडिया संपादक", jobTh: "บรรณาธิการสื่อไลฟ์สไตล์", jobVi: "Biên tập viên Lifestyle", jobId: "Editor Media Gaya Hidup", jobTl: "Editor ng Lifestyle Media", jobNe: "लाइफस्टाइल मीडिया सम्पादक", jobZhTW: "官民合作顧問",
    textJa: "自分でも気づかないうちに、力を入れて頑張り続けていたのかもしれません。名前を見つめ直す時間が、心のメンテナンスに。とにかく自然。「整うってこういうことか」と腑に落ちました。",
    textEn: "I may have been pushing myself without realizing it. Taking time to look at my name became a kind of heart maintenance. It's so natural. I finally understood what 'being in harmony' really means.",
    textIt: "Forse mi stavo spingendo senza rendermene conto. Prendersi del tempo per guardare il mio nome è diventato una sorta di manutenzione del cuore.",
    textFr: "Je me poussais peut-être sans m'en rendre compte. Prendre le temps de regarder mon nom est devenu une sorte d'entretien du cœur.",
    textDe: "Ich arbeite gerne hart, aber mich zu zwingen fühlte sich falsch an. Diese Art der Harmonisierung schiebt mich sanft vorwärts.",
    textEs: "Quizás me estaba esforzando sin darme cuenta. Tomar tiempo para mirar mi nombre se convirtió en una especie de mantenimiento del corazón.",
    textRu: "Даже в мои суматошные дни я замечаю моменты лёгкости.",
    textZh: "即使在忙碌的每一天，也会在某个瞬间感受到「轻盈」。",
    textKo: "노력하는 건 좋아하지만, 억지로 노력하는 건 이제 아니다 싶었습니다.",
    textPt: "Adoro trabalhar duro, mas me forçar parecia errado. Essa forma de harmonizar me empurra suavemente para frente.",
    textAr: "أحب العمل الجاد، لكن إجبار نفسي كان يبدو خاطئاً.",
    textHi: "मुझे मेहनत करना पसंद है, लेकिन खुद को मजबूर करना गलत लगता था।",
    textTh: "ฉันชอบทำงานหนัก แต่การบังคับตัวเองรู้สึกผิด",
    textVi: "Tôi thích làm việc chăm chỉ, nhưng ép buộc bản thân cảm thấy sai.",
    textId: "Saya suka bekerja keras, tetapi memaksakan diri terasa salah.",
    textTl: "Mahilig akong magtrabaho nang husto, ngunit ang pilitin ang sarili ay pakiramdam na mali.",
    textNe: "मलाई लाग्छ कि म आफ्नो समय राम्रोसँग व्यवस्थापन गर्न सक्षम भएको छु।",
    textZhTW: "工作性質決定了語言非常重要。正因如此，重新審視自己名字的時間，出乎意料地有著重大意義。",
  },
  {
    titleJa: "仕事と家庭、どちらも大切にできています",
    titleEn: "I can cherish both work and family",
    titleIt: "Riesco a valorizzare sia il lavoro che la famiglia",
    titleFr: "Je peux chérir à la fois le travail et la famille",
    titleDe: "Ich kann sowohl Arbeit als auch Familie schätzen",
    titleEs: "Puedo valorar tanto el trabajo como la familia",
    titleRu: "Я могу ценить и работу, и семью",
    titleZh: "工作和家庭，两者都能好好珍惜",
    titleKo: "일과 가정, 둘 다 소중히 할 수 있습니다",
    titlePt: "Consigo valorizar tanto o trabalho quanto a família",
    titleAr: "أستطيع الاعتزاز بالعمل والأسرة معاً",
    titleHi: "काम और परिवार दोनों को संजो सकती हूं",
    titleTh: "ฉันสามารถให้ความสำคัญทั้งงานและครอบครัว",
    titleVi: "Tôi có thể trân trọng cả công việc lẫn gia đình",
    titleId: "Saya bisa menghargai baik pekerjaan maupun keluarga",
    titleTl: "Maaari kong pahalagahan ang parehong trabaho at pamilya",
    titleNe: "काम र परिवार दुवैलाई सम्मान गर्न सक्छु",
    titleZhTW: "工作和家庭，兩者都能好好珍惜",
    location: "福岡", locationEn: "Fukuoka", locationIt: "Fukuoka", locationFr: "Fukuoka", locationDe: "Fukuoka", locationEs: "Fukuoka", locationRu: "Фукуока", locationZh: "福冈", locationKo: "후쿠오카", locationPt: "Fukuoka", locationAr: "فوكوكا", locationHi: "फुकुओका", locationTh: "ฟุกุโอกะ", locationVi: "Fukuoka", locationId: "Fukuoka", locationTl: "Fukuoka", locationNe: "फुकुओका", locationZhTW: "福岡",
    age: "40代", ageEn: "40s", ageIt: "40 anni", ageFr: "40 ans", ageDe: "40er", ageEs: "40 años", ageRu: "40-е", ageZh: "40多岁", ageKo: "40대", agePt: "40 anos", ageAr: "الأربعينيات", ageHi: "40 के दशक", ageTh: "อายุ 40 ปี", ageVi: "40 tuổi", ageId: "40-an", ageTl: "40s", ageNe: "४० को दशक", ageZhTW: "40多歲",
    job: "フリーランス美容クリエイター", jobEn: "Freelance Beauty Creator", jobIt: "Creatrice Beauty Freelance", jobFr: "Créatrice Beauté Freelance", jobDe: "Freelance Beauty-Creatorin", jobEs: "Creadora de Belleza Freelance", jobRu: "Фриланс-бьюти-креатор", jobZh: "自由职业美容创作者", jobKo: "프리랜서 뷰티 크리에이터", jobPt: "Criadora de Beleza Freelance", jobAr: "مبدعة جمال مستقلة", jobHi: "फ्रीलांस ब्यूटी क्रिएटर", jobTh: "ครีเอเตอร์ความงามฟรีแลนซ์", jobVi: "Nhà sáng tạo làm đẹp tự do", jobId: "Kreator Kecantikan Freelance", jobTl: "Freelance Beauty Creator", jobNe: "फ्रीलांस ब्यूटी क्रिएटर", jobZhTW: "自由職業美容創作者",
    textJa: "以前より、時間の使い方が上手になった気がします。焦らず、丁寧に、でもちゃんと前に進める。自分の名前を味方にできるって、こんなに安心感があるんですね。",
    textEn: "I feel I've gotten better at managing my time. Unhurried, careful, yet moving forward. Having my own name as an ally brings such a sense of security.",
    textIt: "Sento di essere migliorata nella gestione del mio tempo. Senza fretta, con cura, eppure andando avanti.",
    textFr: "J'ai l'impression de mieux gérer mon temps. Sans précipitation, avec soin, mais en avançant.",
    textDe: "Ich habe das Gefühl, besser mit meiner Zeit umzugehen. Ohne Eile, sorgfältig, aber vorwärtskommend.",
    textEs: "Siento que he mejorado en la gestión de mi tiempo. Sin prisa, con cuidado, pero avanzando.",
    textRu: "Я чувствую, что стала лучше управлять своим временем. Без спешки, внимательно, но двигаясь вперёд.",
    textZh: "感觉比以前更善于利用时间了。不着急，认真地，但确实在向前迈进。",
    textKo: "이전보다 시간 활용이 능숙해진 것 같습니다. 서두르지 않고, 정성스럽게, 하지만 제대로 앞으로 나아갈 수 있습니다.",
    textPt: "Talvez eu estivesse me forçando sem perceber. Dedicar tempo para olhar para o meu nome se tornou uma espécie de manutenção do coração.",
    textAr: "أشعر أنني أصبحت أفضل في إدارة وقتي. بدون تسرع، بعناية، لكن مع التقدم للأمام.",
    textHi: "मुझे लगता है कि मैं अपना समय बेहतर तरीके से प्रबंधित करने लगी हूं।",
    textTh: "ฉันรู้สึกว่าจัดการเวลาได้ดีขึ้น ไม่รีบร้อน ระมัดระวัง แต่ก็ก้าวไปข้างหน้า",
    textVi: "Những ngày của tôi thường bị cuốn vào sự bận rộn, nhưng kể từ khi thêm tenten vào tên, tôi cảm thấy hơi thở của mình sâu hơn.",
    textId: "Hari-hari saya cenderung tersapu oleh kesibukan, tetapi sejak tenten ditambahkan ke nama saya, saya merasa napas saya entah bagaimana menjadi lebih dalam.",
    textTl: "Ang mga araw ay madalas na nadadala ng abala, ngunit mula nang idagdag ang tenten sa aking pangalan, pakiramdam ko ay lumalalim ang aking paghinga.",
    textNe: "मेरा दिनहरू व्यस्ततामा बग्ने गर्छन्, तर नाममा टेन्टेन थपिएपछि, अनौठो रूपमा मेरो सास गहिरो भएको जस्तो लाग्छ।",
    textZhTW: "工作性質決定了語言非常重要。正因如此，重新審視自己名字的時間，出乎意料地有著重大意義。",
  },
  {
    titleJa: "無理に変わろうとしなくていい安心感",
    titleEn: "The relief of not having to force change",
    titleIt: "Il sollievo di non dover forzare il cambiamento",
    titleFr: "Le soulagement de ne pas avoir à forcer le changement",
    titleDe: "Die Erleichterung, keine Veränderung erzwingen zu müssen",
    titleEs: "El alivio de no tener que forzar el cambio",
    titleRu: "Облегчение от того, что не нужно форсировать перемены",
    titleZh: "不必强迫自己改变的安心感",
    titleKo: "억지로 변하려 하지 않아도 된다는 안심감",
    titlePt: "O alívio de não precisar forçar mudanças",
    titleAr: "الارتياح من عدم الاضطرار لإجبار التغيير",
    titleHi: "जबरदस्ती बदलने की जरूरत नहीं — यह राहत",
    titleTh: "ความโล่งใจที่ไม่ต้องบังคับตัวเองให้เปลี่ยนแปลง",
    titleVi: "Sự nhẹ nhõm khi không phải ép buộc thay đổi",
    titleId: "Kelegaan karena tidak harus memaksakan perubahan",
    titleTl: "Ang kaginhawahan ng hindi kailangang pilitin ang pagbabago",
    titleNe: "बदलने की जरूरत नहीं — यह राहत",
    titleZhTW: "不必強迫自己改變的安心感",
    location: "京都", locationEn: "Kyoto", locationIt: "Kyoto", locationFr: "Kyoto", locationDe: "Kyoto", locationEs: "Kioto", locationRu: "Киото", locationZh: "京都", locationKo: "교토", locationPt: "Kyoto", locationAr: "كيوتو", locationHi: "क्योटो", locationTh: "เกียวโต", locationVi: "Kyoto", locationId: "Kyoto", locationTl: "Kyoto", locationNe: "क्योटो", locationZhTW: "京都",
    age: "30代", ageEn: "30s", ageIt: "30 anni", ageFr: "30 ans", ageDe: "30er", ageEs: "30 años", ageRu: "30-е", ageZh: "30多岁", ageKo: "30대", agePt: "30 anos", ageAr: "الثلاثينيات", ageHi: "30 के दशक", ageTh: "อายุ 30 ปี", ageVi: "30 tuổi", ageId: "30-an", ageTl: "30s", ageNe: "३० को दशक", ageZhTW: "30多歲",
    job: "官民連携コンサル", jobEn: "Public-Private Consultant", jobIt: "Consulente Pubblico-Privato", jobFr: "Consultant Public-Privé", jobDe: "Öffentlich-Privat-Beraterin", jobEs: "Consultora Público-Privada", jobRu: "Консультант государственно-частного партнёрства", jobZh: "官民合作顾问", jobKo: "관민 연계 컨설턴트", jobPt: "Consultora Público-Privada", jobAr: "مستشارة شراكة عام-خاص", jobHi: "स्वास्थ्य सेवा प्रबंधक", jobTh: "ที่ปรึกษาภาครัฐ-เอกชน", jobVi: "Tư vấn Công-Tư", jobId: "Konsultan Publik-Swasta", jobTl: "Konsultant ng Publiko-Pribado", jobNe: "स्वास्थ्य सेवा प्रबंधक", jobZhTW: "官民合作顧問",
    textJa: "「変われる」ではなく、「いまの自分が心地よく生きられる」サポート。そんな整い方でした。名前って、静かに寄り添ってくれる存在だったんですね。",
    textEn: "Not 'you can change,' but 'you can live comfortably as you are now.' That's the kind of harmony it was. A name is something that quietly walks beside you.",
    textIt: "Non 'puoi cambiare', ma 'puoi vivere comodamente così come sei ora'. Il tipo di armonia.",
    textFr: "Pas vous pouvez changer, mais vous pouvez vivre confortablement comme vous êtes maintenant.",
    textDe: "Nicht Sie können sich verändern, sondern Sie können bequem so leben, wie Sie jetzt sind.",
    textEs: "No puedes cambiar, sino puedes vivir cómodamente tal como eres ahora.",
    textRu: "Не «ты можешь измениться», а «ты можешь жить комфортом такой, какая ты есть сейчас».",
    textZh: "不是「你可以改变」，而是「以现在的自己舒适地生活」的支持。",
    textKo: "'変る'가 아니라, '지금의 나로 편안하게 살 수 있다'는 지원.",
    textPt: "Não 'você pode mudar', mas 'você pode viver confortavelmente como é agora'.",
    textAr: "ليس 'يمكنك التغيير'، بل 'يمكنك العيش بشكل مريح كما أنت الآن'.",
    textHi: "'आप बदल सकते हैं' नहीं, बरु 'आप अहिले जस्तो हुनुहुन्छ त्यसरी नै आरामसाथ बाँच्न सक्नुहुन्छ।'",
    textTh: "ไม่ใช่ 'คุณสามารถเปลี่ยนแปลงได้' แต่ 'คุณสามารถใช้ชีวิตอย่างสบายใจในแบบที่คุณเป็นตอนนี้'",
    textVi: "Không gian xuất hiện trong việc nuôi dạy con",
    textId: "Bukan 'kamu bisa berubah', melainkan 'kamu bisa hidup nyaman seperti dirimu sekarang'.",
    textTl: "Hindi 'maaari kang magbago', kundi 'maaari kang mabuhay nang komportable tulad ng ikaw ngayon'.",
    textNe: "सायद म आफैलाई थाहा नभई जोड गरिरहेको थिएँ।",
    textZhTW: "工作性質決定了語言非常重要。正因如此，重新審視自己名字的時間，出乎意料地有著重大意義。",
  },
];

function FemaleAvatar({ size = "lg" }: { size?: "lg" | "sm" }) {
  const outer = size === "lg" ? "w-20 h-20" : "w-10 h-10";
  const svgSize = size === "lg" ? 32 : 16;
  return (
    <div className={`${outer} flex items-center justify-center rounded-full bg-[#d4f3f1] border-2 border-[#0ABAB5]/30 flex-shrink-0`}>
      <svg width={svgSize} height={svgSize} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="6" r="4" stroke="#0ABAB5" strokeWidth="1.5" fill="#0ABAB5" fillOpacity="0.2" />
        <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke="#0ABAB5" strokeWidth="1.5" strokeLinecap="round" fill="#0ABAB5" fillOpacity="0.2" />
      </svg>
    </div>
  );
}

const VISIBLE = 6;

export default function TestimonialsSection() {
  const { t, i18n } = useTranslation();
  const [featured, setFeatured] = useState(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: featuredRef, isVisible: featuredVisible } = useScrollAnimation();
  const { setRef, visibleItems } = useScrollAnimationMultiple(VISIBLE, { threshold: 0.06 });

  const lang = i18n.language;

  const getTitle = (item: typeof testimonials[0]) => {
    if (lang === "zh-TW") return item.titleZhTW;
    if (lang === "ne") return item.titleNe;
    if (lang === "tl") return item.titleTl;
    if (lang === "id") return item.titleId;
    if (lang === "vi") return item.titleVi;
    if (lang === "th") return item.titleTh;
    if (lang === "hi") return item.titleHi;
    if (lang === "ar") return item.titleAr;
    if (lang === "pt") return item.titlePt;
    if (lang === "ko") return item.titleKo;
    if (lang === "zh") return item.titleZh;
    if (lang === "ru") return item.titleRu;
    if (lang === "es") return item.titleEs;
    if (lang === "de") return item.titleDe;
    if (lang === "fr") return item.titleFr;
    if (lang === "it") return item.titleIt;
    if (lang === "en") return item.titleEn;
    return item.titleJa;
  };
  const getText = (item: typeof testimonials[0]) => {
    if (lang === "zh-TW") return item.textZhTW;
    if (lang === "ne") return item.textNe;
    if (lang === "tl") return item.textTl;
    if (lang === "id") return item.textId;
    if (lang === "vi") return item.textVi;
    if (lang === "th") return item.textTh;
    if (lang === "hi") return item.textHi;
    if (lang === "ar") return item.textAr;
    if (lang === "pt") return item.textPt;
    if (lang === "ko") return item.textKo;
    if (lang === "zh") return item.textZh;
    if (lang === "ru") return item.textRu;
    if (lang === "es") return item.textEs;
    if (lang === "de") return item.textDe;
    if (lang === "fr") return item.textFr;
    if (lang === "it") return item.textIt;
    if (lang === "en") return item.textEn;
    return item.textJa;
  };
  const getLocation = (item: typeof testimonials[0]) => {
    if (lang === "zh-TW") return item.locationZhTW;
    if (lang === "ne") return item.locationNe;
    if (lang === "tl") return item.locationTl;
    if (lang === "id") return item.locationId;
    if (lang === "vi") return item.locationVi;
    if (lang === "th") return item.locationTh;
    if (lang === "hi") return item.locationHi;
    if (lang === "ar") return item.locationAr;
    if (lang === "pt") return item.locationPt;
    if (lang === "ko") return item.locationKo;
    if (lang === "zh") return item.locationZh;
    if (lang === "ru") return item.locationRu;
    if (lang === "es") return item.locationEs;
    if (lang === "de") return item.locationDe;
    if (lang === "fr") return item.locationFr;
    if (lang === "it") return item.locationIt;
    if (lang === "en") return item.locationEn;
    return item.location;
  };
  const getAge = (item: typeof testimonials[0]) => {
    if (lang === "zh-TW") return item.ageZhTW;
    if (lang === "ne") return item.ageNe;
    if (lang === "tl") return item.ageTl;
    if (lang === "id") return item.ageId;
    if (lang === "vi") return item.ageVi;
    if (lang === "th") return item.ageTh;
    if (lang === "hi") return item.ageHi;
    if (lang === "ar") return item.ageAr;
    if (lang === "pt") return item.agePt;
    if (lang === "ko") return item.ageKo;
    if (lang === "zh") return item.ageZh;
    if (lang === "ru") return item.ageRu;
    if (lang === "es") return item.ageEs;
    if (lang === "de") return item.ageDe;
    if (lang === "fr") return item.ageFr;
    if (lang === "it") return item.ageIt;
    if (lang === "en") return item.ageEn;
    return item.age;
  };
  const getJob = (item: typeof testimonials[0]) => {
    if (lang === "zh-TW") return item.jobZhTW;
    if (lang === "ne") return item.jobNe;
    if (lang === "tl") return item.jobTl;
    if (lang === "id") return item.jobId;
    if (lang === "vi") return item.jobVi;
    if (lang === "th") return item.jobTh;
    if (lang === "hi") return item.jobHi;
    if (lang === "ar") return item.jobAr;
    if (lang === "pt") return item.jobPt;
    if (lang === "ko") return item.jobKo;
    if (lang === "zh") return item.jobZh;
    if (lang === "ru") return item.jobRu;
    if (lang === "es") return item.jobEs;
    if (lang === "de") return item.jobDe;
    if (lang === "fr") return item.jobFr;
    if (lang === "it") return item.jobIt;
    if (lang === "en") return item.jobEn;
    return item.job;
  };

  const displayed = testimonials.slice(0, VISIBLE);

  return (
    <section id="testimonials" className="py-16 sm:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-10 sm:mb-16 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-5" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            {t("testimonials_heading")}
          </h2>
        </div>

        {/* Featured card — モバイルは縦積み */}
        <div
          ref={featuredRef as React.RefObject<HTMLDivElement>}
          className={`bg-[#e8f9f8] rounded-2xl border border-[#0ABAB5]/20 p-5 sm:p-8 md:p-10 mb-8 sm:mb-10 flex flex-col gap-4 md:flex-row md:gap-8 items-start transition-all duration-700 ease-out ${featuredVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ transitionDelay: featuredVisible ? "100ms" : "0ms" }}
        >
          <div className="flex-shrink-0 flex flex-row md:flex-col items-center gap-3">
            <FemaleAvatar size="lg" />
            <div className="text-left md:text-center">
              <p className="text-xs font-semibold text-gray-700">{getLocation(testimonials[featured])}</p>
              <p className="text-xs text-gray-400">{getAge(testimonials[featured])} / {getJob(testimonials[featured])}</p>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex gap-0.5 mb-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <i key={i} className="ri-star-fill text-[#0ABAB5] text-sm" />
              ))}
            </div>
            <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-3 leading-snug" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              &ldquo;{getTitle(testimonials[featured])}&rdquo;
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">{getText(testimonials[featured])}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {displayed.map((item, i) => (
            <button
              key={i}
              ref={setRef(i) as unknown as React.RefObject<HTMLButtonElement>}
              onClick={() => setFeatured(i)}
              className={`text-left bg-white rounded-2xl border p-5 transition-all duration-500 ease-out cursor-pointer hover:-translate-y-0.5 ${
                featured === i ? "border-[#0ABAB5] ring-1 ring-[#0ABAB5]/30" : "border-gray-100 hover:border-[#0ABAB5]/30"
              } ${visibleItems[i] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: visibleItems[i] ? `${i * 60}ms` : "0ms" }}
            >
              <div className="flex items-center gap-3 mb-4">
                <FemaleAvatar size="sm" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-700 leading-tight truncate">{getLocation(item)} / {getAge(item)}</p>
                  <p className="text-xs text-gray-400 leading-tight truncate">{getJob(item)}</p>
                </div>
                <div className="ml-auto flex gap-0.5 flex-shrink-0">
                  {Array.from({ length: 5 }).map((_, si) => (
                    <i key={si} className="ri-star-fill text-[#0ABAB5] text-xs" />
                  ))}
                </div>
              </div>
              <p className="text-sm font-bold text-gray-800 mb-2 leading-snug" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                &ldquo;{getTitle(item)}&rdquo;
              </p>
              <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">{getText(item)}</p>
              {featured === i && (
                <div className="mt-3 flex items-center gap-1 text-[#0ABAB5] text-xs font-medium">
                  <i className="ri-eye-line text-xs" />
                  {t("testimonials_viewing")}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
